import { createSlice } from '@reduxjs/toolkit';
import {
  OCCUPATION_LARGE_KEY,
  OccupationLargeKey,
  HaQueryInfo,
} from '@doda/common/src/constants/seo/occupationConstants';

export type OccupationBarGraphData = {
  [key in OccupationLargeKey]: {
    data: number[];
    labels: string[];
    searchJobTypeIds: string[];
  };
};

/**
 * OccupationRankingState interface.
 * @property publicationDate - The publication date of the ranking.
 * @property occupationBarGraph - The occupation bar graph data.
 * @property haQueryInfo - The HA query information.
 * @property rangeFlagName - The range flag name.
 */
export interface OccupationRankingState {
  publicationDate: string;
  occupationBarGraph: OccupationBarGraphData;
  haQueryInfo: HaQueryInfo;
  rangeFlagName: string;
}

/**
 * occupationRankingInitialState
 */
export const occupationRankingInitialState: OccupationRankingState = {
  publicationDate: '',
  occupationBarGraph: OCCUPATION_LARGE_KEY.reduce((acc, key) => {
    return {
      ...acc,
      [key]: {
        data: [],
        labels: [],
        searchJobTypeIds: [],
      },
    };
  }, {} as OccupationBarGraphData),
  haQueryInfo: {} as HaQueryInfo,
  rangeFlagName: '',
};

/**
 * occupationRankingSlice
 */
const occupationRankingSlice = createSlice({
  name: 'occupationRanking',
  initialState: occupationRankingInitialState,
  reducers: {
    setOccupationBarGraph: (state, action) => {
      state.occupationBarGraph = action.payload;
    },
    setPublicationDate: (state, action) => {
      state.publicationDate = action.payload;
    },
    setHaQueryInfo: (state, action) => {
      state.haQueryInfo = action.payload;
    },
    setRangeFlagName: (state, action) => {
      state.rangeFlagName = action.payload;
    },
  },
});

// Create Action
export const {
  setOccupationBarGraph,
  setPublicationDate,
  setHaQueryInfo,
  setRangeFlagName,
} = occupationRankingSlice.actions;

// reducer
export const occupationRankingReducer = occupationRankingSlice.reducer;

interface State {
  jobOccupationRanking: OccupationRankingState;
}

/**
 * Selector function to retrieve the occupation ranking state.
 * @param state - The state object containing the jobOccupationRanking state.
 * @returns The occupation ranking state.
 */
export const occupationRankingSelector = <S extends State>(state: S) =>
  state.jobOccupationRanking;
